import React, { useEffect } from 'react';
import { IonButton, IonCol, IonContent, IonGrid, IonRow, IonText } from '@ionic/react';
import { useInstance } from '../support/ioc/useInstance';
import { useSubscription } from '@autopylot-internal/state';
import { RevenueCatModel } from './revenue-cat/RevenueCatModel.ts';
import { CheckmarkIcon, CloseIcon } from '../components/icons/Icons.tsx';
import { PaywallDialogViewModel } from './PaywallDialogViewModel.tsx';
import { useBreakpoints } from '../support/hooks/useBreakpoints.tsx';
import { PaymentTermButton } from './PaymentTermsButton.tsx';
import { AutoPylotPlus } from './revenue-cat/AutoPylotPlus.tsx';
import { Color } from '../components/common/index.ts';
import { PageFooter } from '../components/page/PageFooter.tsx';
import { useIsNative } from '../support/hooks/useIsNative.tsx';
import { TrackingService } from '../support/tracking/TrackingService.ts';
import { formatElementAsText } from '../support/helpers/format.ts';

const YesIcon: React.FC<{color: Color}> = ({ color }) => (
    <CheckmarkIcon color={color} size="medium" strokeWidth={75} />
);

const NoIcon: React.FC<{color: Color}> = ({ color }) => (
    <CloseIcon color={color} size="medium" strokeWidth={5} />
);

const BenefitsHeader: React.FC = () => (
    <div style={{fontSize: 14, display: 'flex', alignItems: 'center', marginBottom: -1}}>
        <span style={{flexGrow: 1}}/>
        <span style={{width: 65, textAlign: 'center', fontSize: 15, padding: '10px 5px 5px'}}><b>Free</b></span>
        <span style={{width: 95, textAlign: 'center', fontSize: 15, padding: '10px 5px 5px', borderTopLeftRadius: 10, borderTopRightRadius: 10, color: 'white', backgroundColor: 'var(--ion-color-primary)'}}><b>AutoPylot+</b></span>
    </div>
);

const Benefit: React.FC<{plusOnly?: boolean, last?: boolean, children: React.ReactNode}> = ({ plusOnly, last, children }) => (
    <div style={{display: 'flex', alignItems: 'center', height: 35, borderBottom: (last ? undefined : '1px solid lightgray')}}>
        <IonText color={plusOnly ? 'primary' : 'dark'} style={{fontSize: 14, fontWeight: 'bold', flexGrow: 1, marginRight: -20, overflowX: 'hidden'}}>{children}</IonText>
        <div style={{flexShrink: 0, width: 65, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>{plusOnly ? <NoIcon color="medium" /> : <YesIcon color="primary" />}</div>
        <div style={{flexShrink: 0, width: 95, height: '100%', display: 'flex', alignItems: 'center', paddingBottom: last ? 5 : 0, borderBottomLeftRadius: last ? 10 : undefined, borderBottomRightRadius: last ? 10 : undefined, justifyContent: 'center', backgroundColor: 'var(--ion-color-primary)'}}><YesIcon color="light" /></div>
    </div>
);

const Benefits: React.FC = () => (
    <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'space-around', height: '100%', marginTop: 10}}>
        <BenefitsHeader />
        <Benefit>B4UFLY and LAANC</Benefit>
        <Benefit>Create and assign checklists</Benefit>
        <Benefit>Manage missions</Benefit>
        <Benefit plusOnly>Unlimited planning weather</Benefit>
        <Benefit plusOnly>Windy maps and charts</Benefit>
        <Benefit plusOnly>Share and export missions</Benefit>
        <Benefit plusOnly>Auto-assign drones to missions</Benefit>
        <Benefit plusOnly last>Log and track drone activity</Benefit>
    </div>
);

export const NewUserDialogContent: React.FC<{onUpgrade: () => void, onRestore: () => void}> = ({ onUpgrade, onRestore }) => {
    const revenueCat = useInstance(RevenueCatModel);
    useSubscription(() => revenueCat);

    const dialogVM = useInstance(PaywallDialogViewModel);
    useSubscription(() => dialogVM);

    const {isBelowBreakpoint} = useBreakpoints();

    const isNative = useIsNative();

    const meta = revenueCat.getOfferMeta().paywallInfo;

    const monthlyPriceString = meta?.monthlyPriceString || '';
    const annualPriceString = (!isNative && meta?.annualPriceStringWeb) ? meta?.annualPriceStringWeb : meta?.annualPriceString || '';
    const annualDiscountBadge = (!isNative && meta?.annualDiscountBadgeWeb) ? meta?.annualDiscountBadgeWeb : meta?.annualDiscountBadge;

    const trialDays = revenueCat.getTrialDays();
    const trialPrefix = `${trialDays} days free, then `;

    const annualPricePerMonth = Math.round(Number(annualPriceString?.substring(1))/12*100)/100;
    const annualSubtext = `${trialPrefix}$${annualPricePerMonth}/mo, billed annually`;
    const monthlySubtext = `${trialPrefix}${monthlyPriceString}, billed monthly`;

    const term = dialogVM.state.paymentTerm;

    const title = <>Plan smarter, fly safe, save hours, get <AutoPylotPlus /> <IonText color="primary">free for {trialDays} days</IonText></>;
    const buttonText = `Get AutoPylot+ free for ${trialDays} days`;

    const tracking = useInstance(TrackingService);

    useEffect(() => {
        if (!revenueCat.hasUpgraded()) {
            tracking.track('Paywall Opened', () => ({
                from: dialogVM.getTriggerSource(),
                paywallTitle: formatElementAsText(title),
                annualPrice: annualPriceString.substring(1),
                monthlyPrice: monthlyPriceString.substring(1),
                buttonText
            }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onContinue = () => {
        tracking.track('Paywall Continued', () => ({
            term,
            from: dialogVM.getTriggerSource(),
            paywallTitle: formatElementAsText(title),
            annualPrice: annualPriceString.substring(1),
            monthlyPrice: monthlyPriceString.substring(1),
            buttonText
        }));
        onUpgrade();
    };

    return (
        <>
            <IonContent>
                <div style={{margin: 'calc(var(--ion-safe-area-top) + 28px) 10px 0'}}>

                    <h1 style={{fontWeight: 'bold', fontSize: isBelowBreakpoint('sm') ? 20 : 24, margin: '0 45px 0 8px', height: 55, overflow: 'visible'}}>{title}</h1>

                    <IonGrid>
                        <IonRow class="ion-align-items-center ion-justify-content-between">

                            <IonCol size="12" sizeMd="7">
                                <Benefits />
                            </IonCol>

                            <IonCol size="12" sizeMd="5" style={{marginTop: 20}}>
                                <PaymentTermButton
                                    disabled={dialogVM.state.isWorking}
                                    isActive={term === 'annual'}
                                    onClick={() => dialogVM.setState({ paymentTerm: 'annual' })}
                                    termLabel={`Yearly Plan - ${annualPriceString}`}
                                    termDescription={annualSubtext}
                                    badge={annualDiscountBadge}
                                    badgeAlignment="left"
                                />
                                <PaymentTermButton
                                    disabled={dialogVM.state.isWorking}
                                    isActive={term === 'monthly'}
                                    onClick={() => dialogVM.setState({ paymentTerm: 'monthly' })}
                                    termLabel={`Monthly Plan - ${monthlyPriceString}`}
                                    termDescription={monthlySubtext}
                                />
                            </IonCol>

                        </IonRow>
                    </IonGrid>

                </div>
            </IonContent>

            <PageFooter shadow>
                <div style={{margin: '12px 30px 0'}}>

                    <IonButton
                        disabled={dialogVM.state.isWorking}
                        expand="block"
                        fill="solid"
                        color="primary"
                        onClick={() => onContinue()}
                        data-id="continue"
                    >
                        <span>{buttonText}</span>
                    </IonButton>

                    <div style={{width: '100%', textAlign: 'center', marginTop: 10}}>
                        <IonText color="medium">Cancel any time, we'll remind you</IonText>
                    </div>

                    <div style={{width: '100%', maxWidth: 260, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <IonButton disabled={dialogVM.state.isWorking} size="small" expand="block" fill="clear" color="secondary" href="https://www.autopylot.io/terms" target="_blank">Terms of Use</IonButton>
                        {revenueCat.canRestore() &&
                            <IonButton expand="block" size="small" fill="clear" color="secondary" onClick={onRestore} data-id="restore">Restore Purchases</IonButton>
                        }
                    </div>

                </div>
            </PageFooter>
        </>
    );
};
